<script setup lang="ts">
import {
  OBJECT_CATEGORIES,
  type ObjectCategory,
  type ObjectCategoryFilter,
} from "@/composable/useObjectsCatalog";

/**
 * Filtre par catégorie : des pastilles qui passent à la ligne, plutôt que des
 * onglets qui ne tiendraient pas à 5 dans la largeur du volet. Des boutons
 * bascule (une seule enfoncée) : chacun s'atteint à la tabulation.
 */
defineProps<{
  modelValue: ObjectCategoryFilter;
  counts: Record<ObjectCategory, number>;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: ObjectCategoryFilter): void;
}>();
</script>

<template>
  <div
    class="objects-chips"
    role="group"
    :aria-label="$t('canvas:settings.objects.categoriesLabel')"
  >
    <button
      type="button"
      class="objects-chip"
      :class="{ active: modelValue === 'all' }"
      :aria-pressed="modelValue === 'all'"
      @click="emit('update:modelValue', 'all')"
    >
      {{ $t("canvas:settings.objects.categories.all") }}
    </button>
    <button
      v-for="category in OBJECT_CATEGORIES"
      :key="category"
      type="button"
      class="objects-chip"
      :class="{ active: modelValue === category, empty: !counts[category] }"
      :aria-pressed="modelValue === category"
      @click="emit('update:modelValue', category)"
    >
      {{ $t(`canvas:settings.objects.categories.${category}`) }}
      <span class="chip-count">{{ counts[category] }}</span>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.objects-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.objects-chip {
  display: flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid var(--canvas-border);
  border-radius: 15px;
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 0.15s,
    border-color 0.15s;

  &:hover:not(.active) {
    background-color: var(--canvas-btn-hover-bg);
  }

  &.empty:not(.active) {
    color: rgba(255, 255, 255, 0.4);
  }

  &.active {
    border-color: var(--canvas-btn-active-bg);
    background-color: var(--canvas-btn-active-bg);
    color: #1a1a1a;
    font-weight: 600;
  }
}

.chip-count {
  min-width: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 10.5px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;

  .objects-chip.active & {
    background: rgba(0, 0, 0, 0.15);
  }
}
</style>
